import { useCallback, useEffect, useState } from "react";
import { getDatasetFolderTree, type DatasetFolderTree } from "@/api/datasetFolder";
import type { DatasetStorageBackend } from "@/api/datasetStorageBackend";
import { createLogger } from "@/logging";
import { resolveLoadErrorMessage, type LoadState } from "../loadState";

const logger = createLogger("components/dataset/folder");

export type UseDatasetFolderTreeResult = {
  state: LoadState<DatasetFolderTree>;
  // 받은 트리의 건수가 지금 저장 위치의 것인지다. 저장 위치를 바꾼 직후에는 새 트리를 받을 때까지
  // false 다. 그동안 트리의 건수는 이전 저장 위치의 것이다. 모집단이 다른 수는 표시하지 않는다
  // (docs/rules.md §17).
  countsCurrent: boolean;
  // 트리를 다시 조회한다. 폴더를 쓰거나(생성·이름 변경·이동·삭제) 데이터셋을 옮기면 건수가
  // 바뀐다. 그래서 호출부가 명시적으로 호출한다.
  reload: () => void;
};

/**
 * organization 폴더 트리 로드 + 명시적 재조회.
 *
 * 행이 수백 개 규모인 얕은 트리이므로 한 번에 전체를 받는다. 노드를 펼칠 때 하위를 따로
 * 조회하지 않는다(파일 브라우저의 디렉터리 트리와 다른 점).
 *
 * 저장 위치가 바뀌면 트리를 다시 받는다. 폴더 목록은 저장 위치와 무관하고 건수만 다르다. 그래서
 * 다시 받는 동안 받은 트리를 그대로 돌려주고 `countsCurrent` 만 false 로 둔다. 호출부는 폴더 목록이
 * 바뀐 뒤에 `reload` 를 부른다. 그래서 `reload` 는 loading 으로 돌아간다.
 *
 * @param storageBackend 건수를 셀 저장 위치. 목록의 저장 위치 게이트와 같은 값이다. null 이면 모든
 *   저장 위치를 센다. 예) `"azure_blob"`
 * @returns 로드 상태, 건수가 지금 저장 위치의 것인지, 재조회 함수.
 */
export function useDatasetFolderTree(
  storageBackend: DatasetStorageBackend | null,
): UseDatasetFolderTreeResult {
  const [state, setState] = useState<LoadState<DatasetFolderTree>>({ status: "idle" });
  // 받은 트리의 건수를 센 저장 위치다. `state` 가 success 일 때만 뜻이 있다.
  const [countsStorageBackend, setCountsStorageBackend] = useState<DatasetStorageBackend | null>(
    null,
  );
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((current) => (current.status === "success" ? current : { status: "loading" }));
    void getDatasetFolderTree({ storageBackend })
      .then((data) => {
        if (cancelled) return;
        setState({ status: "success", data });
        setCountsStorageBackend(storageBackend);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error("folderTree failed", error);
        setState({ status: "error", message: resolveLoadErrorMessage(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken, storageBackend]);

  const reload = useCallback(() => {
    setState({ status: "loading" });
    setReloadToken((current) => current + 1);
  }, []);
  return {
    state,
    countsCurrent: state.status === "success" && countsStorageBackend === storageBackend,
    reload,
  };
}
